import React, { FC, PropsWithChildren, ReactNode, useMemo, useState } from 'react';
import { ICourseData, ICourseInstance, ICoursePricing } from '../../../models/course/ICourseData';
import CourseKeyFacts from '../../molecules/course-keyfacts/course-keyfacts';
import { replaceAttrSpaceWithHyphen } from '../../../utils/string-helpers/replace-attr-space-with-hyphen';
import Markdown from 'markdown-to-jsx';
import ImageComponent from '../../../components/atoms/image-component/image-component';
import CourseDetails from '../../../components/molecules/course-details/course-details';
import MediaGallery from '../../../components/molecules/media-gallery/media-gallery';
import Button from '../../../components/atoms/button/button';
import { ButtonStyleType } from '../../../constants/button-style-type';
import { ICoursePageSettings } from '../../../models/course/ICoursePageSettings';
import MediaGalleryComponentModel from '../../../models/components/media-gallery-component-model';
import { ServiceLevelType } from '../../../constants/service-level-type';
import IconTooltip from '../../molecules/icon-tooltip/icon-tooltip';
import { SLAIcon } from '../../atoms/icons/icons';
import {
  preferredProgramAccessibilityLabel,
  preferredProgramTooltipContent,
  preferredProgramTooltipHeading,
} from '../../../data/preferred-program';
import { formatCourseInstanceDate } from '../../../utils/string-helpers/formatting.helper';
import { BrandOptionFields, ICourseContent } from '../../../models/course/ICourseContent';
import styles from './course-page-template.module.scss';
import breakpoints from '../../../styles/partials/breakpoints.module.scss';
import useWindowResize from '../../../hooks/use-window-resize.hook';
import { deviceDetect } from '../../../data/device-detect';
import colorStyle from '../../../styles/utils/variables.module.scss';
import SidebarNavigation from '../../molecules/sidebar-navigation/sidebar-navigation';
import Link from 'next/link';
import { MediaAssetType } from '../../../constants/media-asset-type';
import AdvertBanner from '../../molecules/advert-banner/advert-banner';
import { ApplicationBranding } from '../../../models/settings';
import { checkAdvertBannerContentExists } from '../../../utils/advert-banner/advert-banner-content';
import TagManager from 'react-gtm-module';
import { ApplicationType } from '../../../constants/application-type';
import { getCourseSubMenu } from '../../../utils/jumplink-helpers/course-jumpLink';
import { useTranslations } from 'next-intl';
import ModalBox from '../../molecules/modal-box/modal-box';
import IButton from '../../../models/button/IButton';

export type Props = {
  coursePageSettings: ICoursePageSettings;
  courseContent: ICourseContent;
  courseData: ICourseData;
  courseInstance: ICourseInstance[];
  children?: ReactNode;
  pagePath?: string;
  locales?: string[];
  application?: string;
  googleTagId?: string;
  iCasUndergraduateExists?: boolean;
  iCasGraduateExists?: boolean;
  currentLocale?: string;
};

export default function CoursePage({
  coursePageSettings,
  courseContent,
  courseData,
  courseInstance: courseInstances,
  children,
  pagePath,
  locales,
  application,
  googleTagId,
  iCasUndergraduateExists,
  iCasGraduateExists,
  currentLocale,
}: Props) {
  const translate = useTranslations();

  const courseSubMenu = useMemo(
    () => (application == ApplicationType.UniBrandedB2c ? getCourseSubMenu(translate) : null),
    [application, translate],
  );

  // customized brand options from CFv2
  // diff naming for duration etc.
  const brandOptions: BrandOptionFields = courseContent?.brand?.fields?.brandOptions?.fields;

  if (!brandOptions?.navigation && application !== ApplicationType.UniBrandedB2c) {
    return (
      <CoursePageElements
        coursePageSettings={coursePageSettings}
        courseContent={courseContent}
        courseData={courseData}
        courseInstances={courseInstances}
        application={application}
        googleTagId={googleTagId}
        iCasUndergraduateExists={iCasUndergraduateExists}
        iCasGraduateExists={iCasGraduateExists}
        currentLocale={currentLocale}
      >
        {children}
      </CoursePageElements>
    );
  }

  return (
    <div className='custom-page-template'>
      <div className='row'>
        <div className='col-lg-3 px-0 px-lg-3'>
          <SidebarNavigation
            sidebarNavigationProps={
              application == ApplicationType.UniBrandedB2c
                ? courseSubMenu
                : brandOptions?.navigation?.fields
            }
            sidebarBrandColor={coursePageSettings.backgroundColor}
            b2cFlag={coursePageSettings.b2cFlag}
            pagePath={pagePath}
            locales={locales}
          />
        </div>
        <div className='col-lg-9 py-lg-4 py-0'>
          <CoursePageElements
            coursePageSettings={coursePageSettings}
            courseContent={courseContent}
            courseData={courseData}
            courseInstances={courseInstances}
            application={application}
            googleTagId={googleTagId}
            iCasUndergraduateExists={iCasUndergraduateExists}
            iCasGraduateExists={iCasGraduateExists}
            currentLocale={currentLocale}
          >
            {children}
          </CoursePageElements>
        </div>
      </div>
    </div>
  );
}

type CoursePageElementsProps = {
  coursePageSettings: ICoursePageSettings;
  courseContent: ICourseContent;
  courseData: ICourseData;
  courseInstances: ICourseInstance[];
  children?: ReactNode;
  pagePath?: string;
  locales?: string[];
  application?: string;
  googleTagId?: string;
  iCasUndergraduateExists?: boolean;
  iCasGraduateExists?: boolean;
  currentLocale?: string;
  applicationFee?: ICoursePricing;
} & PropsWithChildren;

const CoursePageElements: FC<CoursePageElementsProps> = ({
  coursePageSettings,
  courseContent,
  courseData,
  courseInstances,
  children,
  application,
  googleTagId,
  iCasUndergraduateExists,
  iCasGraduateExists,
  currentLocale,
}) => {
  const [isBreakpointUp, setIsBreakpointUp] = useState(!deviceDetect.isMobile);

  useWindowResize(
    (newWidth) => setIsBreakpointUp(newWidth >= parseInt(breakpoints.tablet_landscape, 10)),
    100,
  );

  // get minimum tuitionFee from the Course Instances Set
  const tuitionFee =
    courseInstances?.length > 0
      ? courseInstances?.filter((a) => typeof a.tuitionFee !== 'undefined').length > 0
        ? courseInstances
            .filter((a) => typeof a.tuitionFee !== 'undefined')
            .map((a) => a.tuitionFee)
            .reduce((prev, current) => {
              return prev?.amount < current?.amount ? prev : current;
            })
        : null
      : null;

  // get Requirements from the longest duration Course Instance
  const courseInstance =
    courseInstances?.length > 0
      ? courseInstances?.reduce((prev, current) => {
          return parseInt(prev.duration?.split('MONTHS')[0]) >
            parseInt(current.duration?.split('MONTHS')[0])
            ? prev
            : current;
        })
      : null;

  // get all the start dates from the Course Instances Set
  // sort them by Month and Year
  // show only unique values
  let courseInstancesStartDates: string[] = [];
  let tempInstancesStartDates: string[] = [];
  courseInstances?.forEach((instance) => {
    tempInstancesStartDates.push(instance?.startDate);
  });
  courseInstancesStartDates = formatCourseInstanceDate(tempInstancesStartDates);

  // customized brand options from CFv2
  // diff naming for duration etc.
  const brandOptions: BrandOptionFields = courseContent?.brand?.fields?.brandOptions?.fields;

  // get Course Synonym as per Brand Options / Country Code
  const getCourseSynonym = (): string => {
    if (brandOptions?.programName) {
      return brandOptions.programName;
    }

    return courseContent.brand.fields.countryCode === 'US' ? 'Program' : 'Course';
  };

  // construct media gallery data
  let mediaGalleryComponent: any;
  if (courseContent.mediaGallery?.fields?.gallery) {
    mediaGalleryComponent = new MediaGalleryComponentModel(
      courseContent.mediaGallery?.fields,
      null,
    );
  }

  // check if advert banner data exist
  let checkAdvertExists = checkAdvertBannerContentExists(courseContent.advertBanner?.fields);

  const courseCTABranding: ApplicationBranding = {
    primaryColor: coursePageSettings.backgroundColor,
    primaryHoverColor: coursePageSettings.hoverBackgroundColor,
    primaryActiveColor: coursePageSettings.activeBackgroundColor,
  };

  // for redirect modal
  const [showModal, setShowModal] = useState(false);

  const pushDataLayer = () => {
    if (googleTagId) {
      TagManager.dataLayer({
        dataLayer: {
          brandName: courseContent.brand.fields.brandName,
          courseName: courseData.name,
          event: 'applyButtonClick',
        },
      });
    }

    // for redirect modal
    if (application == ApplicationType.UniBrandedB2c) {
      setShowModal(true);
    }
  };

  const buttonProps = {
    buttonStyle: ButtonStyleType.Filled,
    buttonSize: coursePageSettings.buttonSize,
    copyColor: coursePageSettings.buttonCopyColor,
    backgroundColor: coursePageSettings.backgroundColor,
    hoverBackgroundColor: coursePageSettings.hoverBackgroundColor,
    activeBackgroundColor: coursePageSettings.activeBackgroundColor,
    buttonAriaLabel: courseData?.name,
    heapIdentifier: 'new-course-page-apply',
    children: 'Apply',
    buttonID: 'course-details-apply-cta',
    onClick: pushDataLayer,
  };

  const modalFooterButton: IButton = {
    ...buttonProps,
    buttonURL: coursePageSettings.applyURL,
    buttonRole: 'link',
    buttonID: 'course-details-modalbox-apply-cta',
    buttonName: 'course-details-modalbox-apply-cta',
    buttonAriaLabel: `Apply to ${courseData?.name}`,
    onClick: () => setShowModal(false),
    children: coursePageSettings?.redirectModal?.buttonText,
  };

  return (
    <>
      {application == ApplicationType.UniBrandedB2c && (
        <ModalBox
          isOpen={showModal}
          title={coursePageSettings?.redirectModal?.title}
          onClose={() => setShowModal(false)}
          footerButton={modalFooterButton}
          autoClickAfterDelay
          modalSize='lg'
          modalVerticallyCenter
        >
          {coursePageSettings?.redirectModal?.message}
        </ModalBox>
      )}
      <section>
        {children && (
          <div className='course-page-extra'>
            {/* Passing SEO tags from upper level as next/head component doesn't work from here */}
            {children}
          </div>
        )}
        <div className={`${styles[`course-page-title`]} mb-md-4 mb-3`}>
          <div className='row'>
            <div className={brandOptions?.navigation ? 'col' : 'col-md-9'}>
              {!!courseData && (
                <h2 className={styles[`course-name`]}>
                  {courseData?.name}
                  {isBreakpointUp &&
                    application == ApplicationType.PartnerPortal &&
                    courseData?.serviceLevel?.includes(ServiceLevelType.Premium) && (
                      <IconTooltip
                        svgIcon={SLAIcon()}
                        tooltipAccessibilityLabel={preferredProgramAccessibilityLabel}
                        heading={preferredProgramTooltipHeading}
                        svgIconColor={colorStyle.red100}
                        activeSVGIconColor={colorStyle.red200}
                        heapIdentifier={replaceAttrSpaceWithHyphen(preferredProgramTooltipHeading)}
                        heapDataContent={courseData?.name}
                        helperClass='ms-2'
                      >
                        <Markdown>{preferredProgramTooltipContent()}</Markdown>
                      </IconTooltip>
                    )}
                </h2>
              )}
              <h5 className={styles[`university-name`]}>
                <Link href={coursePageSettings.brandURL}>
                  {courseContent?.brand?.fields.brandName}
                </Link>
              </h5>
              {checkAdvertExists &&
              application === 'PartnerPortal' &&
              (courseContent.showMediaAsset === MediaAssetType.AdvertBanner ||
                courseContent.showMediaAsset === MediaAssetType.Both) ? (
                <div className='mb-md-4 mb-3'>
                  <AdvertBanner
                    advertBanner={courseContent.advertBanner.fields}
                    appBranding={courseCTABranding}
                    application={application}
                  />
                </div>
              ) : mediaGalleryComponent != null &&
                (courseContent.showMediaAsset === MediaAssetType.MediaGallery ||
                  courseContent.showMediaAsset === MediaAssetType.Both) ? (
                <div className='row'>
                  <div className='col-md-12 gallery'>
                    <MediaGallery
                      galleryItems={mediaGalleryComponent?.gallery}
                      hash={typeof window !== 'undefined' ? window.location?.hash : null}
                    />
                  </div>
                </div>
              ) : (
                <></>
              )}
              {!!courseContent.shortDescription?.length && (
                <div className={styles[`course-definition`]}>
                  <div className='row'>
                    <div className='col-md-12'>
                      <section className={styles[`course-definition`]}>
                        <Markdown>{courseContent.shortDescription}</Markdown>
                      </section>
                    </div>
                  </div>
                </div>
              )}
              <div className='mt-2'>
                {application == ApplicationType.UniBrandedB2c ? (
                  <Button {...buttonProps} />
                ) : (
                  <Button
                    {...buttonProps}
                    buttonNewTab='_blank'
                    buttonURL={coursePageSettings.applyURL}
                    buttonRole='link'
                  />
                )}
              </div>
            </div>
            {courseContent?.brand?.fields?.colorLogo?.fields?.image?.length &&
              !brandOptions?.navigation && (
                <div className='col-md-3'>
                  <Link
                    className='d-lg-block d-none text-center'
                    href={coursePageSettings.brandURL}
                  >
                    <ImageComponent
                      src={courseContent?.brand?.fields?.colorLogo?.fields?.image[0]?.secure_url}
                      altText={courseContent?.brand?.fields?.colorLogo?.fields?.altText}
                      width={255}
                      height={170}
                      layout='fixed'
                      objectFit='contain'
                    />
                  </Link>
                </div>
              )}
          </div>
        </div>
        <div className={`${styles[`course-page-key-information`]} mb-md-4 mb-3`}>
          <CourseKeyFacts
            courseName={courseData?.name}
            coursePageSettings={coursePageSettings}
            courseInstanceRequirements={courseInstance?.requirements}
            courseInstanceTuitionFee={tuitionFee}
            courseInstancesStartDates={courseInstancesStartDates}
            keyInformationRichText={courseContent.keyInformation}
            courseSynonym={getCourseSynonym()}
          />
        </div>
        <div className={`${styles[`course-page-details`]} mb-md-4 mb-3`}>
          {courseData != null && courseInstances?.length > 0 && (
            <CourseDetails
              courseInstances={courseInstances}
              courseData={courseData}
              coursePageSettings={coursePageSettings}
              brandName={courseContent?.brand?.fields.brandName}
              courseName={courseData?.name}
              courseContent={courseContent}
              courseSynonym={getCourseSynonym()}
              googleTagId={googleTagId}
              iCasUndergraduateExists={iCasUndergraduateExists}
              iCasGraduateExists={iCasGraduateExists}
              currentLocale={currentLocale}
              application={application}
              buttonProps={buttonProps}
            />
          )}
        </div>
      </section>
    </>
  );
};
